import { BaseModal, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import React, { useEffect, useState } from 'react';
import { ReactComponent as SplitScreenIcon } from 'assets/icons/split-screen.svg';
import NoScreens from './no-screens';
import ScreenCard from './screen-card';
import CreateScreenModal from './create-screen-modal';
import { FormDataType, ScreenType } from 'models/screen.model';
import { STORAGE_KEYS } from 'utils/constants';
import { apiService } from 'utils/api';
import { toast } from 'react-toastify';

type Props = {
  show: boolean;
  onClose: () => void;
};

const SplitScreen = ({ show, onClose }: Props) => {
  const [screens, setScreens] = useState<ScreenType[]>([]);
  const [filteredScreens, setFilteredScreens] = useState<ScreenType[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateScreenModal, setShowCreateScreenModal] = useState(false);
  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN);
  const role = localStorage.getItem(STORAGE_KEYS.ROLE);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res: { data: ScreenType[] } = await apiService.get(
        `kitchen/screens?cokitchen_id=${cokitchenId ?? ''}`,
        { baseUrl: process.env.REACT_APP_KITCHEN_API_URL }
      );
      setScreens(res?.data);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  const handleEditSuccess = (value: FormDataType) => {
    const screenIndex = screens.findIndex((item) => item.id === value.id);
    if (screenIndex !== -1) {
      const editedScreen = {
        ...screens[screenIndex],
        ...value
      };
      const newScreens = [...screens];
      newScreens.splice(screenIndex, 1, editedScreen);
      setScreens(newScreens);
    }
  };

  const handleDeleteSuccess = (id: string) => {
    const unDeletedScreen = screens.filter((item) => item.id !== id);
    setScreens(unDeletedScreen);
  };

  const handleSearch = () => {
    const filterScreens =
      search === '' ? screens : screens.filter((screen) => screen.name.includes(search));
    setFilteredScreens(filterScreens);
  };

  useEffect(() => {
    handleSearch();
  }, [search, screens]);

  return (
    <BaseModal
      size='wide'
      isOpen={show}
      onRequestClose={onClose}
      title='Split Screens'
      titleCta={
        screens.length > 0 && role?.includes('ADMIN') ? (
          <p
            className='text-[#E60A2B] cursor-pointer text-sm font-medium'
            onClick={() => setShowCreateScreenModal(true)}>
            + Add new screen
          </p>
        ) : undefined
      }
      titleIcon={<SplitScreenIcon />}
      titleIconColor='success'>
      <Input
        type='search'
        value={search}
        placeholder='Search by screen number '
        small
        onChange={(e) => setSearch(e.target.value)}
        className='mb-7'
      />
      <div>
        {loading ? (
          <div className='text-sm flex items-center justify-center'>Loading...</div>
        ) : filteredScreens.length === 0 ? (
          <NoScreens setShow={setShowCreateScreenModal} onClose={onClose} />
        ) : (
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {filteredScreens
              .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
              .map((item, index) => (
                <ScreenCard
                  data={item}
                  key={index}
                  onEditSuccess={handleEditSuccess}
                  onDeleteSuccess={handleDeleteSuccess}
                />
              ))}
          </div>
        )}
      </div>

      <CreateScreenModal
        createScreenData={{
          name: '',
          active: false,
          brand_ids: []
        }}
        type='create'
        isOpen={showCreateScreenModal}
        onClose={() => setShowCreateScreenModal(false)}
        refreshData={fetchData}
      />
    </BaseModal>
  );
};

export default SplitScreen;
